"use client";

import React from "react";
import axios from "axios";
import { useRouter } from "next/navigation";

import { useToast } from "@/hooks/ui/useToast";
import { useApiWithLayout } from "@/hooks/api";
import { useDomainConfig } from "@/hooks/domain/useDomainConfig";

import { LayoutContext } from "@/contexts/LayoutContext";
import { INITIAL_QR_DATA, QREditorContext } from "@/contexts/QREditorContext";
import { UserContext, USER_INITIAL_DATA } from "@/contexts/UserContext";

import postLoginPostHandler from "@/interface/api/postLogin";
import getProfileHandler from "@/interface/api/getProfile";

import { clearPendingActionCookie } from "@/actions/clearPendingActionCookie";
import { userProfileApiToDataTransformer } from "@/services/UserService";
import { ApiErrorMessageBuilder } from "@/utils/apiErrorHandler";
import { isClient } from "@/utils/browser";
import { paths } from "@/utils/paths";
import {
  amplitudeLogoutUser,
  amplitudeTrackUser,
} from "@/services/AmplitudeService/AmplitudeService";
import {
  getAuthCookie,
  removeAuthCookie,
  removeCookie,
  setAuthCookie,
} from "@/services/CookiesService";

import { PostLoginResponseData } from "@/interface/api/postLogin/types";
import { QR_REFERENCE_COOKIE } from "@/containers/AuthContainer/types";

import { GetProfileResponse } from "@/interface/api/getProfile/types";
import { UTMS } from "@/components/StoreUtms/types";

const onHandleLogin = ({ email, password, request, reference }) => {
  request({
    email,
    password,
    reference,
  }).finally(() => {
    removeCookie(QR_REFERENCE_COOKIE);
  });
};

export const isUserLoggedIn = () => getAuthCookie();

export const isValidToken = async (callback?: () => void, onError?: () => void) => {
  const auth = getAuthCookie();
  const call = async () => {
    return axios.get(`${process.env.NEXT_PUBLIC_API_BASE_URL}/me/profile`, {
      headers: {
        Authorization: `Bearer ${auth}`,
      },
    });
  };
  try {
    if (callback) {
      await call();
      callback();
    }
  } catch {
    onError && onError();
  }
  return call;
};

export const useLogin = () => {
  const { toast } = useToast();
  const router = useRouter();
  const { logoutRedirectPath } = useDomainConfig();

  const layoutContext = React.useContext(LayoutContext);
  const userContext = React.useContext(UserContext);
  const qrEditorContext = React.useContext(QREditorContext);

  const [loginData, setLoginData] = React.useState(undefined);

  const { request, loading, data, error } = useApiWithLayout<PostLoginResponseData>(
    postLoginPostHandler.postLogin
  );
  const { request: requestProfile, loading: isProfileLoading } =
    useApiWithLayout<GetProfileResponse>(getProfileHandler.getProfile);

  const onSaveCookie = (token: string, tokenExpiration: string) => {
    if (!token) return;
    setAuthCookie(token, tokenExpiration);
  };

  const onHandleProfile = async () => {
    const token = getAuthCookie();
    requestProfile({ authorization: token })
      .then((response: { data: GetProfileResponse }) => {
        const profile = response?.data;

        if (profile) {
          amplitudeTrackUser(profile?.id);
          const formattedUser = userProfileApiToDataTransformer(profile);

          userContext?.setUser({
            ...USER_INITIAL_DATA,
            ...formattedUser,
          });
        }
        layoutContext?.setIsLoading(false);
        setLoginData(data);
        return data;
      })
      .catch((error) => {
        const errorMessage = ApiErrorMessageBuilder.buildToastMessage(error);
        toast({ message: errorMessage, type: "error" });
        layoutContext?.setIsLoading(false);
        return undefined;
      });
  };

  React.useEffect(() => {
    if (data?.status === 200) {
      const token = data?.data?.token;
      const tokenExpiration = data?.data?.expiration;

      onSaveCookie(token, tokenExpiration);
      layoutContext?.setIsLoading(false);
      setLoginData(data);

      // Pre-fetching the my QR Codes page ONLY after login for better performance and avoid missing cookie issues
      router.prefetch(paths.myQrCodes());
    }
  }, [data]);

  const onHandleLogout = (redirectToLogin?: boolean) => {
    if (!isClient()) return;

    layoutContext?.setIsLoading(true);
    userContext?.setUser(USER_INITIAL_DATA);
    qrEditorContext?.setQrData(INITIAL_QR_DATA);

    removeAuthCookie();
    removeCookie(UTMS);
    clearPendingActionCookie();
    amplitudeLogoutUser();

    if (redirectToLogin) {
      router.push(paths.login());
    } else {
      router.push(logoutRedirectPath);
    }

    layoutContext?.setIsLoading(false);
  };

  return {
    onHandleLogin: ({ email, password, reference }) =>
      onHandleLogin({ email, password, request, reference }),
    onHandleLogout,
    isValidToken: () => isValidToken(request),
    isUserLoggedIn,
    onSaveCookie,
    onHandleProfile,
    isLoginLoading: loading || isProfileLoading,
    loginResponse: loginData,
    loginError: error,
  };
};
